import {
  Automation,
  Trigger as TriggerModel,
  Action as ActionModel,
} from "@/types/v2/Automation";
import { Trigger } from "@/components/Automations/Trigger";
import { Action } from "@/components/Automations/Action";
import { CreateTaskActionValue } from "@/components/Automations/CreateTaskActionValue";
import { ScheduleValue } from "@/components/Automations/ScheduleValue";
import { ShowToastActionValue } from "@/components/Automations/ShowToastActionValue";
import { ScheduleScopeAlert } from "@/components/Automations/ScheduleScopeAlert";
import { isUnconditionedSchedule } from "@/lib/automationSchedule";
import {
  AUTOMATION_ACTION_OPTIONS,
  AUTOMATION_TRIGGER_OPTIONS,
} from "@/lib/const";
import { Fragment } from "react";
import { flattenTriggers } from "@/lib/flattenTriggers";
import { useDynamicOptions } from "@/hooks/useDynamicOptions";
import { flattenActions } from "@/lib/flattenActions";

export interface AutomationDisplayProps {
  automation: Automation;
  isEditing?: boolean;
  onEdit?: (automation: Automation) => void;
}
export const AutomationDisplay = ({
  automation,
  isEditing,
  onEdit,
}: AutomationDisplayProps) => {
  const { generateOptions } = useDynamicOptions();
  const handleTriggerChange = (trigger: TriggerModel, index: number) => {
    const oldAutomation = { ...automation };
    const oldTriggers = [...oldAutomation.triggers];
    oldTriggers[index] = trigger;
    if (onEdit) {
      onEdit({ ...oldAutomation, triggers: oldTriggers });
    }
  };

  const handleActionChange = (action: ActionModel, index: number) => {
    const oldAutomation = { ...automation };
    const oldActions = [...oldAutomation.actions];
    oldActions[index] = action;
    if (onEdit) {
      onEdit({ ...oldAutomation, actions: oldActions });
    }
  };

  const renderTrigger = (trigger: TriggerModel, index: number) => {
    const templateTrigger = flattenTriggers(AUTOMATION_TRIGGER_OPTIONS).find(
      (t) => t.id === trigger.templateId
    );

    if (templateTrigger?.triggerValueEditor === "schedule") {
      return (
        <ScheduleValue
          label={
            templateTrigger.buttonOptions.selectedText ??
            templateTrigger.buttonOptions.text ??
            ""
          }
          value={trigger.triggerValue}
          onChange={
            isEditing
              ? (raw) =>
                  handleTriggerChange({ ...trigger, triggerValue: raw }, index)
              : undefined
          }
          readOnly={!isEditing}
          canRemove={false}
        />
      );
    }

    return (
      <Trigger
        label={
          templateTrigger?.buttonOptions.selectedText ??
          templateTrigger?.buttonOptions.text ??
          ""
        }
        valueValue={
          trigger.triggerValue ? `${trigger.triggerValue}` : undefined
        }
        canRemove={false}
        canAdd={false}
        disabled={!isEditing}
        valueOptions={generateOptions(
          templateTrigger?.triggerValueOptions,
          templateTrigger?.triggerValueOptionsDynamic
        )}
        // Gate on the catalog, not on the saved value — but only when the
        // catalog can actually answer. A falsy saved value (a container never
        // chosen, "labor greater than 0") is still editable, and a template
        // marked triggerValueEditable: false must never render a control —
        // this mirrors renderAction's actionValueEditable gate. However, a
        // saved automation is a snapshot: the backend evaluates it by its
        // stored templateId regardless of whether that id still exists in
        // AUTOMATION_TRIGGER_OPTIONS. If the template can't be resolved (it
        // was removed/renamed from the catalog), we must not treat that as
        // "not editable" — fall back to the pre-branch truthiness check so
        // the row never loses its control just because we can't look up its
        // definition anymore.
        onValueChange={
          templateTrigger
            ? templateTrigger.triggerValueEditable
              ? (value) =>
                  handleTriggerChange(
                    { ...trigger, triggerValue: value },
                    index
                  )
              : undefined
            : trigger.triggerValue
              ? (value) =>
                  handleTriggerChange(
                    { ...trigger, triggerValue: value },
                    index
                  )
              : undefined
        }
      />
    );
  };

  const renderAction = (action: ActionModel, index: number) => {
    const templateAction = flattenActions(AUTOMATION_ACTION_OPTIONS).find(
      (t) => t.id === action.templateId
    );

    if (templateAction?.actionField === "createTask") {
      return (
        <CreateTaskActionValue
          label={
            templateAction.buttonOptions.selectedText ??
            templateAction.buttonOptions.text ??
            ""
          }
          value={action.actionValue}
          assigneeOptions={generateOptions(undefined, "assignees") ?? []}
          onChange={
            isEditing
              ? (raw) =>
                  handleActionChange({ ...action, actionValue: raw }, index)
              : undefined
          }
          readOnly={!isEditing}
          canRemove={false}
        />
      );
    }

    if (templateAction?.actionField === "showToast") {
      return (
        <ShowToastActionValue
          label={
            templateAction.buttonOptions.selectedText ??
            templateAction.buttonOptions.text ??
            ""
          }
          value={action.actionValue}
          onChange={
            isEditing
              ? (raw) =>
                  handleActionChange({ ...action, actionValue: raw }, index)
              : undefined
          }
          readOnly={!isEditing}
          canRemove={false}
        />
      );
    }

    return (
      <Action
        label={
          templateAction?.buttonOptions.selectedText ??
          templateAction?.buttonOptions.text ??
          ""
        }
        valueValue={
          templateAction?.actionValueEditable && action.actionValue
            ? `${action.actionValue}`
            : undefined
        }
        valueOptions={generateOptions(
          templateAction?.actionValueOptions,
          templateAction?.actionValueOptionsDynamic
        )}
        multiSelect={templateAction?.multiSelect}
        canRemove={false}
        canAdd={false}
        disabled={!isEditing}
        // Gate on the catalog, not on the saved value. A falsy saved value (a
        // timer minutes field the user just cleared to "") must stay
        // editable, and a template marked actionValueEditable: false must
        // never render a control — this mirrors renderTrigger's
        // triggerValueEditable gate above.
        onValueChange={
          templateAction?.actionValueEditable
            ? (value) =>
                handleActionChange({ ...action, actionValue: value }, index)
            : undefined
        }
      />
    );
  };
  return (
    <div className={"flex w-full flex-col gap-2"}>
      <div
        className={
          "flex w-full flex-row flex-wrap items-center gap-2 rounded-lg bg-primary-foreground px-4 py-2"
        }
      >
        {automation.triggers.map((trigger, index) => (
          <Fragment key={`${trigger.templateId}-${index}`}>
            <div key={`${trigger.templateId}-${index}`}>
              {renderTrigger(trigger, index)}
            </div>
            {index + 1 < automation.triggers.length && (
              <span className={"mr-2 font-mono text-sm text-primary/80"}>
                {automation.triggersConditional}
              </span>
            )}
          </Fragment>
        ))}
        <div
          className={
            "mr-2 flex flex-row items-center font-mono text-sm text-primary/80"
          }
        >
          THEN
        </div>
        {automation.actions.map((action, index) => (
          <Fragment key={`${action.templateId}-${index}`}>
            <div key={`${action.templateId}-${index}`}>
              {renderAction(action, index)}
            </div>
          </Fragment>
        ))}
      </div>
      {isUnconditionedSchedule(automation.triggers) && <ScheduleScopeAlert />}
    </div>
  );
};
